import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Ban,
  Boxes,
  Download,
  ShoppingCart,
  Wallet,
} from 'lucide-react';
import { useTenantLocale } from '@/hooks/useTenantLocale';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { reportsService, type SalesPeriodGrouping } from '@/services/reports';
import { fmt } from '@/lib/format';

const PILL_TRIGGER =
  'inline-flex h-9 items-center gap-1.5 rounded-md border border-transparent bg-transparent px-3 text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground data-[state=active]:border-border data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-elev-1';

export function ReportsPage() {
  return (
    <div className="space-y-4">
      <ListPageHeader title="Reports" />

      <Tabs defaultValue="sales" className="space-y-4">
        <TabsList className="bg-muted/40 p-1">
          <TabsTrigger value="sales" className={PILL_TRIGGER}>
            <ShoppingCart className="h-3.5 w-3.5" />
            Sales by period
          </TabsTrigger>
          <TabsTrigger value="stock" className={PILL_TRIGGER}>
            <Boxes className="h-3.5 w-3.5" />
            Stock valuation
          </TabsTrigger>
          <TabsTrigger value="refunds" className={PILL_TRIGGER}>
            <Wallet className="h-3.5 w-3.5" />
            Refund summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="m-0">
          <SalesByPeriodReport />
        </TabsContent>
        <TabsContent value="stock" className="m-0">
          <StockValuationReport />
        </TabsContent>
        <TabsContent value="refunds" className="m-0">
          <RefundSummaryReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Report 1: Sales by period ──────────────────────────────────────────

function SalesByPeriodReport() {
  const today = new Date();
  const ninetyDaysAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(ninetyDaysAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [groupBy, setGroupBy] = useState<SalesPeriodGrouping>('DAY');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports', 'sales', { from, to, groupBy }],
    queryFn: () => reportsService.salesByPeriod({ from, to, groupBy }),
  });

  const rows = data?.rows ?? [];

  const { currency: tenantCurrency } = useTenantLocale();

  const summary = useMemo(() => {
    // Revenue = RECOGNISED revenue: DELIVERED orders only (money actually
    // earned). Open = confirmed orders still in the pipeline (not yet delivered,
    // not cancelled, not draft). CANCELLED = voided (never revenue). DRAFT is
    // not a real order. Always grouped by currency — never summed across.
    const revenueByCur = new Map<string, number>();
    const openByCur = new Map<string, number>();
    const cancelledByCur = new Map<string, number>();
    let deliveredOrders = 0;
    let openOrders = 0;
    let cancelledOrders = 0;
    const add = (m: Map<string, number>, cur: string, amt: number) =>
      m.set(cur, (m.get(cur) ?? 0) + amt);
    for (const r of rows) {
      const st = r.status.toUpperCase();
      if (st === 'DELIVERED') {
        add(revenueByCur, r.currency, r.totalAmount);
        deliveredOrders += r.orderCount;
      } else if (st === 'CANCELLED') {
        add(cancelledByCur, r.currency, r.totalAmount);
        cancelledOrders += r.orderCount;
      } else if (st === 'DRAFT') {
        // not a confirmed order — excluded everywhere
      } else {
        add(openByCur, r.currency, r.totalAmount);
        openOrders += r.orderCount;
      }
    }
    const label = (m: Map<string, number>) =>
      m.size === 0
        ? fmt.money(0, tenantCurrency)
        : Array.from(m.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([cur, amt]) => fmt.money(amt, cur))
            .join(' · ');
    return {
      revenueLabel: label(revenueByCur),
      openLabel: label(openByCur),
      cancelledLabel: label(cancelledByCur),
      deliveredOrders,
      openOrders,
      cancelledOrders,
    };
  }, [rows, tenantCurrency]);

  return (
    <div className="space-y-4">
      <FilterRow>
        <DateField label="From" value={from} onChange={setFrom} />
        <DateField label="To" value={to} onChange={setTo} />
        <div className="space-y-1">
          <label className="text-[11px] text-muted-foreground" htmlFor="groupBy">
            Group by
          </label>
          <Select value={groupBy} onValueChange={(v) => setGroupBy(v as SalesPeriodGrouping)}>
            <SelectTrigger id="groupBy" className="h-8 w-[140px] text-[12.5px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DAY">Day</SelectItem>
              <SelectItem value="WEEK">Week</SelectItem>
              <SelectItem value="MONTH">Month</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DownloadButton href={reportsService.csvUrl('sales', { from, to, groupBy })} />
      </FilterRow>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <KpiCard
          label="Revenue"
          value={summary.revenueLabel}
          hint={`Delivered orders only · ${fmt.number(summary.deliveredOrders)} order${summary.deliveredOrders === 1 ? '' : 's'}`}
          tone="primary"
          icon={Wallet}
        />
        <KpiCard
          label="Open orders"
          value={summary.openLabel}
          hint={`Confirmed, not yet delivered · ${fmt.number(summary.openOrders)} order${summary.openOrders === 1 ? '' : 's'}`}
          tone="default"
          icon={ShoppingCart}
        />
        <KpiCard
          label="Cancelled"
          value={summary.cancelledLabel}
          hint={`${fmt.number(summary.cancelledOrders)} order${summary.cancelledOrders === 1 ? '' : 's'} — not revenue`}
          tone="warning"
          icon={Ban}
        />
      </div>

      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border/70">
          <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            <div className="col-span-3">Period</div>
            <div className="col-span-3">Status</div>
            <div className="col-span-3 text-right">Orders</div>
            <div className="col-span-3 text-right">Revenue</div>
          </div>
          {isLoading ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-muted-foreground">
              No orders in this period.
            </div>
          ) : (
            rows.map((r, i) => (
              <div
                key={`${r.bucket}-${r.status}-${i}`}
                className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2 text-[12.5px] last:border-0"
              >
                <div className="col-span-3 font-mono text-[12px]">{r.bucket}</div>
                <div className="col-span-3">
                  <Badge variant="muted" className="text-[10px]">
                    {r.status.toLowerCase().replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div className="col-span-3 text-right font-mono tabular-nums">
                  {fmt.number(r.orderCount)}
                </div>
                <div className="col-span-3 text-right font-mono tabular-nums">
                  {fmt.money(r.totalAmount, r.currency)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Report 2: Stock valuation ──────────────────────────────────────────

function StockValuationReport() {
  // Inventory cost is held in the tenant's base currency (cost prices carry no
  // per-item currency), so valuation renders in that single currency.
  const { currency } = useTenantLocale();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports', 'stock-valuation'],
    queryFn: () => reportsService.stockValuation(),
  });

  const rows = data?.rows ?? [];
  const perWh = data?.totalsPerWarehouse ?? [];

  return (
    <div className="space-y-4">
      <FilterRow>
        <DownloadButton href={reportsService.csvUrl('stock-valuation')} />
      </FilterRow>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <KpiCard
          label="Total valuation"
          value={fmt.money(data?.grandTotal ?? 0, currency)}
          tone="primary"
          icon={Wallet}
        />
        <KpiCard
          label="SKUs valued"
          value={fmt.number(rows.length)}
          tone="default"
          icon={Boxes}
        />
        <KpiCard
          label="Unpriced (excluded)"
          value={fmt.number(data?.unpricedItemCount ?? 0)}
          tone={data?.unpricedItemCount ? 'warning' : 'default'}
          icon={Boxes}
        />
      </div>

      {perWh.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border/70">
          <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            <div className="col-span-9">Warehouse</div>
            <div className="col-span-3 text-right">Valuation</div>
          </div>
          {perWh.map((w) => (
            <div
              key={w.warehouseId}
              className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2 text-[12.5px] last:border-0"
            >
              <div className="col-span-9 flex items-center gap-2.5">
                <span className="font-mono text-[10.5px] text-muted-foreground">
                  {w.warehouseCode}
                </span>
                <span>{w.warehouseName}</span>
              </div>
              <div className="col-span-3 text-right font-mono font-medium tabular-nums">
                {fmt.money(w.valuation, currency)}
              </div>
            </div>
          ))}
        </div>
      )}

      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border/70">
          <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            <div className="col-span-2">Warehouse</div>
            <div className="col-span-5">Item</div>
            <div className="col-span-2 text-right">Qty on hand</div>
            <div className="col-span-1 text-right">Cost</div>
            <div className="col-span-2 text-right">Valuation</div>
          </div>
          {isLoading ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-muted-foreground">
              No stock on hand.
            </div>
          ) : (
            rows.map((r) => (
              <div
                key={`${r.warehouseId}-${r.itemId}`}
                className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2 text-[12.5px] last:border-0"
              >
                <div className="col-span-2 font-mono text-[10.5px] text-muted-foreground">
                  {r.warehouseCode}
                </div>
                <div className="col-span-5 min-w-0">
                  <div className="truncate font-medium">{r.itemName}</div>
                  <div className="font-mono text-[10.5px] text-muted-foreground">{r.sku}</div>
                </div>
                <div className="col-span-2 text-right font-mono tabular-nums">
                  {fmt.number(r.quantityOnHand)}
                </div>
                <div className="col-span-1 text-right font-mono tabular-nums text-muted-foreground">
                  {r.costPrice == null ? '—' : fmt.money(r.costPrice, currency)}
                </div>
                <div className="col-span-2 text-right font-mono tabular-nums">
                  {r.costPrice == null ? '—' : fmt.money(r.valuation, currency)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Report 3: Refund summary ───────────────────────────────────────────

function RefundSummaryReport() {
  const today = new Date();
  const yearAgo = new Date(today.getFullYear(), today.getMonth() - 11, 1);
  const [from, setFrom] = useState(yearAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports', 'refunds', { from, to }],
    queryFn: () => reportsService.refundsSummary({ from, to }),
  });

  const { currency: tenantCurrency } = useTenantLocale();
  const rows = data?.rows ?? [];
  const totals = data?.totals ?? [];
  // Per-currency total label (never sum across currencies).
  const totalLabel = (pick: (t: (typeof totals)[number]) => number) =>
    totals.length === 0
      ? fmt.money(0, tenantCurrency)
      : totals.map((t) => fmt.money(pick(t), t.currency)).join(' · ');

  return (
    <div className="space-y-4">
      <FilterRow>
        <DateField label="From" value={from} onChange={setFrom} />
        <DateField label="To" value={to} onChange={setTo} />
        <DownloadButton href={reportsService.csvUrl('refunds', { from, to })} />
      </FilterRow>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <KpiCard
          label="Captured"
          value={totalLabel((t) => t.captured)}
          tone="success"
          icon={Wallet}
        />
        <KpiCard
          label="Refunded"
          value={totalLabel((t) => t.refunded)}
          tone={totals.some((t) => t.refunded > 0) ? 'warning' : 'default'}
          icon={Wallet}
        />
        <KpiCard
          label="Net"
          value={totalLabel((t) => t.net)}
          tone="primary"
          icon={Wallet}
        />
      </div>

      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border/70">
          <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            <div className="col-span-3">Month</div>
            <div className="col-span-3 text-right">Captured</div>
            <div className="col-span-3 text-right">Refunded</div>
            <div className="col-span-3 text-right">Net</div>
          </div>
          {isLoading ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-muted-foreground">
              No payment activity in this window.
            </div>
          ) : (
            rows.map((r) => (
              <div
                key={`${r.month}-${r.currency}`}
                className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2 text-[12.5px] last:border-0"
              >
                <div className="col-span-3 font-mono text-[12px]">
                  {r.month}
                  <span className="ml-1.5 text-[10px] text-muted-foreground">{r.currency}</span>
                </div>
                <div className="col-span-3 text-right font-mono tabular-nums">
                  {fmt.money(r.capturedAmount, r.currency)}
                </div>
                <div className="col-span-3 text-right font-mono tabular-nums text-muted-foreground">
                  {fmt.money(r.refundedAmount, r.currency)}
                </div>
                <div
                  className={`col-span-3 text-right font-mono font-medium tabular-nums ${
                    r.netAmount >= 0 ? 'text-foreground' : 'text-destructive'
                  }`}
                >
                  {fmt.money(r.netAmount, r.currency)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Shared filter primitives ───────────────────────────────────────────

function FilterRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-border/70 bg-card px-3 py-3">
      {children}
    </div>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] text-muted-foreground">{label}</label>
      <Input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-[150px] text-[12.5px]"
      />
    </div>
  );
}

function DownloadButton({ href }: { href: string }) {
  return (
    <Button asChild size="sm" variant="outline" className="ml-auto">
      <a href={href} download>
        <Download className="h-3.5 w-3.5" />
        Download CSV
      </a>
    </Button>
  );
}
